import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { User, UserSettings } from '@app/auth/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl: string = environment.apiBaseUrl;
    private pendingLegalAcceptance = signal(false);

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  login(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}login`, data);
  }

  setPendingLegalAcceptance(value: boolean): void {
    this.pendingLegalAcceptance.set(value);
  }

  isPendingLegalAcceptance(): boolean {
    return this.pendingLegalAcceptance();
  }

  logout(): void {
    this.http.post(`${this.baseUrl}logout`, {}).subscribe({
      next: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  private clearSession(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    this.router.navigate(['/']);
  }

  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('login_event', Date.now().toString());
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && !this.pendingLegalAcceptance();
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  checkUserExistsByEmail(email: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}user-exists-by-email?email=${encodeURIComponent(email)}`
    );
  }

  changePassword(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}reset-password`, data, {
      headers: { skipAuth: 'true' },
    });
  }

  setUserToStorage(user: User | null | undefined): void {
    if (!user) {
      localStorage.removeItem('user');
      return;
    }

    localStorage.setItem('user', JSON.stringify(user));
  }

  getUserFromStorage(): User | null {
    const rawUser = localStorage.getItem('user');

    if (!rawUser || rawUser === 'undefined' || rawUser === 'null') {
      return null;
    }

    try {
      const user = JSON.parse(rawUser);
      return user?.id ? user : null;
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  }

  getUserSettingsFromStorage(): UserSettings | null {
    const user = this.getUserFromStorage();
    return user?.settings ?? null;
  }

  getUserPlanFromStorage(): any {
    const user = this.getUserFromStorage();
    return user?.plan ?? null;
  }

  getUserSettings(): Observable<any> {
    return this.http.get(`${this.baseUrl}user/settings`);
  }

  updateUserSettings(data: Partial<UserSettings>): Observable<any> {
    return this.http.patch(`${this.baseUrl}user/settings`, data);
  }

  setUserSettingsToStorage(settings: UserSettings): void {
    const user = this.getUserFromStorage();

    if (!user) {
      return;
    }

    user.settings = settings;
    this.setUserToStorage(user);
  }

  userCanPostLoads(): boolean {
    const user = this.getUserFromStorage();
    return !!user?.can_post_loads;
  }

  userCanPostTrucks(): boolean {
    const user = this.getUserFromStorage();
    return !!user?.can_post_trucks;
  }

  userCanSearchLoads(): boolean {
    const user = this.getUserFromStorage();
    return !!user?.can_search_loads;
  }

  userCanSearchTrucks(): boolean {
    const user = this.getUserFromStorage();
    return !!user?.can_search_trucks;
  }

  getUser(): Observable<any> {
    return this.http.get(`${this.baseUrl}user`);
  }

  // For admin
  updateUser(data: any): Observable<any> {
    const user = this.getUserFromStorage();
    return this.http.patch(`${this.baseUrl}users/${user?.id}`, data);
  }

  // For admin
  deleteUser(): Observable<any> {
    const user = this.getUserFromStorage();
    return this.http.delete(`${this.baseUrl}users/${user?.id}`);
  }

  // User self-service updates
  updateUserName(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}user/name`, data);
  }

  updateUserEmail(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}user/email`, data);
  }

  updateUserPassword(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}user/password`, data);
  }

  updateUserCellPhone(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}user/cell-phone`, data);
  }

  updateUserDirectPhone(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}user/direct-phone`, data);
  }

  deleteAccount(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}user/delete-account`, data);
  }

  getUserConnections(): Observable<any> {
    return this.http.get(`${this.baseUrl}user/carrier-connections`);
  }

  deleteMyConnection(connectionId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}user/carrier-connections/${connectionId}`);
  }

  navigateToDefaultUrl(): void {
    const lastUrl = localStorage.getItem('last_url');

    if (!lastUrl || lastUrl === '/' || lastUrl.startsWith('/login')) {
      this.router.navigate(['/account/connections']);
      return;
    }

    this.router.navigateByUrl(lastUrl).catch(() => {
      localStorage.removeItem('last_url');
      this.router.navigate(['/account/connections']);
    });
  }

  getApiKeys(): Observable<any> {
    return this.http.get(`${this.baseUrl}api-keys`);
  }

  createApiKey(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api-keys`, data);
  }

  deleteApiKey(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}api-keys/${id}`);
  }

  getApiUsage(): Observable<any> {
    return this.http.get(`${this.baseUrl}user/api-usage`);
  }
}